import {
  createContext,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"

import { Tour } from "./Tour"

type TourStep = {
  id?: string
  selector: string | string[]
  title?: ReactNode
  content?: ReactNode
  showNextButton?: boolean
  entryDelay?: number
  exitDelay?: number
  showMask?: boolean
  padding?: number
  side?: "top" | "right" | "bottom" | "left"
  align?: "start" | "center" | "end"
  onEntry?: (args: { direction: "forward" | "backward" }) => void
  onExit?: (args: { direction: "forward" | "backward" }) => void
}

export type TourSteps = [TourStep, ...TourStep[]]

type TourContextContent = {
  start: () => void
  exit: () => void
  next: () => void
  prev: () => void
  goTo: (step: number | string, forceEntryDelay?: number) => void
} & (
  | {
      isActive: true
      step: TourStep
      stepIndex: number
      isTransitioning: boolean
      hasNextStep: boolean
      hasPrevStep: boolean
    }
  | {
      isActive: false
      step: undefined
      stepIndex: undefined
      isTransitioning: false
      hasNextStep: false
      hasPrevStep: false
    }
)

export const TourContext = createContext<TourContextContent>({
  step: undefined,
  stepIndex: undefined,
  isActive: false,
  isTransitioning: false,
  hasNextStep: false,
  hasPrevStep: false,
  start: () => {},
  exit: () => {},
  next: () => {},
  prev: () => {},
  goTo: () => {},
})

type TourProviderProps = {
  steps: TourSteps
  children: ReactNode
  /**
   * When `true`, the tour starts automatically once the first step's target
   * element appears in the DOM. Fires once per provider mount.
   */
  autoStart?: boolean
  /**
   * Milliseconds to wait between detecting the first step's target element
   * and calling `start()`. Only applies when `autoStart` is `true`.
   */
  autoStartDelay?: number
  onStart?: () => void
  onComplete?: () => void
  onExit?: (args: { stepIndex: number | undefined }) => boolean | undefined
}

export function TourProvider(props: TourProviderProps) {
  const {
    steps,
    children,
    autoStart,
    autoStartDelay = 0,
    onStart,
    onComplete,
    onExit,
  } = props

  const [stepIndex, setStepIndex] = useState<number>()
  const [isTransitioning, setIsTransitioning] = useState(false)

  const value = useMemo(() => {
    const step = stepIndex === undefined ? undefined : steps[stepIndex]
    const hasNextStep =
      stepIndex === undefined ? false : stepIndex < steps.length - 1
    const hasPrevStep = stepIndex === undefined ? false : stepIndex > 0
    const isActive = stepIndex !== undefined || isTransitioning

    function start() {
      goTo(0, 0)
      onStart?.()
    }

    function exit() {
      const shouldExit = onExit?.({ stepIndex }) ?? true
      if (shouldExit) reset()
    }

    function next() {
      if (stepIndex === undefined) {
        throw new Error("The tour is not running.")
      }

      if (stepIndex >= steps.length - 1) {
        reset()
        onComplete?.()
      } else {
        goTo(stepIndex + 1)
      }
    }

    function prev() {
      if (stepIndex === undefined) {
        throw new Error("The tour is not running.")
      }

      if (stepIndex < 1) {
        throw new Error("Cannot move to a step less than 0.")
      }

      goTo(stepIndex - 1)
    }

    function goTo(indexOrId: number | string, forceDelay?: number) {
      let newStep
      let newStepIndex: number
      if (typeof indexOrId === "string") {
        newStepIndex = steps.findIndex((step) => step.id === indexOrId)
        newStep = steps[newStepIndex]
        if (!newStep) {
          throw new Error(`No step for id: ${indexOrId}`)
        }
      } else {
        newStep = steps[indexOrId]
        newStepIndex = indexOrId
        if (!newStep) {
          throw new Error(`No step for index: ${indexOrId}`)
        }
      }

      const direction =
        newStepIndex > (stepIndex ?? -1) ? "forward" : "backward"

      step?.onExit?.({ direction })

      const delay =
        forceDelay ?? (step?.exitDelay ?? 0) + (newStep.entryDelay ?? 0)

      if (delay > 0) {
        setIsTransitioning(true)
        setTimeout(() => {
          setStepIndex(newStepIndex)
          setIsTransitioning(false)
        }, delay)
      } else {
        setStepIndex(newStepIndex)
        setIsTransitioning(false)
      }

      newStep.onEntry?.({ direction })
    }

    function reset() {
      setStepIndex(undefined)
      setIsTransitioning(false)
    }

    return {
      step,
      stepIndex,
      isActive,
      isTransitioning,
      hasNextStep,
      hasPrevStep,
      start,
      exit,
      next,
      prev,
      goTo,
    } as TourContextContent
  }, [steps, stepIndex, isTransitioning, onStart, onComplete, onExit])

  const hasAutoStartedRef = useRef(false)
  useEffect(() => {
    if (!autoStart || hasAutoStartedRef.current) return

    const firstStepSelector = steps[0].selector
    const rawSelector = Array.isArray(firstStepSelector)
      ? firstStepSelector[0]
      : firstStepSelector
    if (!rawSelector) return
    const selector: string = rawSelector

    function tryStart() {
      if (!document.querySelector(selector)) return false
      hasAutoStartedRef.current = true
      setTimeout(() => value.start(), autoStartDelay)
      return true
    }

    if (tryStart()) return

    const observer = new MutationObserver(() => {
      if (tryStart()) observer.disconnect()
    })
    observer.observe(document.body, { childList: true, subtree: true })

    return () => observer.disconnect()
  }, [autoStart, autoStartDelay, steps, value])

  return (
    <TourContext.Provider value={value}>
      {children}
      <Tour />
    </TourContext.Provider>
  )
}
